import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Container, Grid, Typography } from '@mui/material'
import { useMutation } from '@apollo/client'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import MuiTextFieldWrapper from 'gui/components/common/MuiTextField'
import MuiCheckbox from 'gui/components/common/MuiCheckbox'
import { TextareaWithCounter } from 'gui/components/textarea-with-counter'
import TextInputFormatSelect, {
  TEXT_FORMAT_RESTRICTED_HTML,
} from 'gui/components/text-input-format-select'
import {
  CONTENT_ANON_UPDATE,
  CONTENT_ANON_CREATE,
  GET_VIDEO_CONTENT,
  UPDATE_VIDEO_CONTENT,
} from './queries'
import type { SectionManagedSectionProps, AnonFormState, ContentAnonData } from './types'
import { useAiInjectedMetadata } from './useAiInjectedMetadata'
import './course-content-information-shared.scss'

const ANON_FIELD_KEYS = [
  'anonShortDescription',
  'anonShortDescriptionFormat',
  'anonDescription',
  'anonDescriptionFormat',
] as const

const SEO_FIELD_KEYS = [
  'seoTitle',
  'seoDescription',
  'seoKeywords',
  'seoRobotsNoFollow',
  'seoRobotsNoIndex',
] as const

const SEO_APPLY_KEYS = ['seoTitle', 'seoDescription', 'seoKeywords'] as const

type SeoFormValues = {
  seoTitle: string
  seoDescription: string
  seoKeywords: string
  seoRobotsNoFollow: boolean
  seoRobotsNoIndex: boolean
}

const initialSeoFormValues: SeoFormValues = {
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  seoRobotsNoFollow: false,
  seoRobotsNoIndex: false,
}

const initialFormValues: AnonFormState = {
  anonShortDescription: '',
  anonShortDescriptionFormat: TEXT_FORMAT_RESTRICTED_HTML,
  anonDescription: '',
  anonDescriptionFormat: TEXT_FORMAT_RESTRICTED_HTML,
}

export interface AnonymousFieldsSectionContentData {
  id: string
  anon?: ContentAnonData | null
  seoTitle?: string | null
  seoDescription?: string | null
  seoKeywords?: string | null
  seoRobotsNoFollow?: boolean | null
  seoRobotsNoIndex?: boolean | null
}

export interface AnonymousFieldsSectionProps extends SectionManagedSectionProps {
  contentData: AnonymousFieldsSectionContentData | null
  /** SEO field names → values injected by another component (e.g. AI Metadata Generation). */
  aiInjectedMetadata?: Record<string, unknown> | null
}

function valuesFromAnon (anon: ContentAnonData | null | undefined): AnonFormState {
  if (!anon) {
    return { ...initialFormValues }
  }
  return {
    anonShortDescription: anon.shortDescription ?? '',
    anonShortDescriptionFormat: anon.shortDescriptionFormat ?? TEXT_FORMAT_RESTRICTED_HTML,
    anonDescription: anon.description ?? '',
    anonDescriptionFormat: anon.descriptionFormat ?? TEXT_FORMAT_RESTRICTED_HTML,
  }
}

function valuesFromSeoContentData (data: AnonymousFieldsSectionContentData | null): SeoFormValues {
  if (!data) return { ...initialSeoFormValues }
  return {
    seoTitle: data.seoTitle ?? '',
    seoDescription: data.seoDescription ?? '',
    seoKeywords: data.seoKeywords ?? '',
    seoRobotsNoFollow: data.seoRobotsNoFollow ?? false,
    seoRobotsNoIndex: data.seoRobotsNoIndex ?? false,
  }
}

/**
 * Content Anon section (section-managed). Contains anon fields + SEO fields.
 * Owns contentAnonUpdate/contentAnonCreate and contentUpdate (SEO); parent calls saveHandlerRef.current() on Save.
 */
function AnonymousFieldsSection ({
  contentData,
  isViewOnly,
  onDirtyChange,
  saveHandlerRef,
  onResetTriggered = 0,
  aiInjectedMetadata,
}: AnonymousFieldsSectionProps) {
  const id = contentData?.id
  const anon = contentData?.anon ?? null

  const [formValues, setFormValues] = useState<AnonFormState>(initialFormValues)
  const [initialValues, setInitialValues] = useState<AnonFormState>(initialFormValues)
  const [seoFormValues, setSeoFormValues] = useState<SeoFormValues>(initialSeoFormValues)
  const [seoInitialValues, setSeoInitialValues] = useState<SeoFormValues>(initialSeoFormValues)
  const saveHandlerRefStable = useRef(saveHandlerRef)
  saveHandlerRefStable.current = saveHandlerRef

  const [contentAnonUpdate] = useMutation(CONTENT_ANON_UPDATE, {
    refetchQueries: id != null ? [{ query: GET_VIDEO_CONTENT, variables: { id } }] : [],
  })

  const [contentAnonCreate] = useMutation(CONTENT_ANON_CREATE, {
    refetchQueries: id != null ? [{ query: GET_VIDEO_CONTENT, variables: { id } }] : [],
  })

  const [updateVideoContent] = useMutation(UPDATE_VIDEO_CONTENT, {
    refetchQueries: id != null ? [{ query: GET_VIDEO_CONTENT, variables: { id } }] : [],
  })

  useEffect(() => {
    const next = valuesFromAnon(anon)
    setFormValues(next)
    setInitialValues(next)
  }, [anon, onResetTriggered])

  useEffect(() => {
    const next = valuesFromSeoContentData(contentData)
    setSeoFormValues(next)
    setSeoInitialValues(next)
  }, [contentData, onResetTriggered])

  useAiInjectedMetadata({
    aiInjectedMetadata,
    allowedKeys: SEO_APPLY_KEYS,
    onApply: (updates) => setSeoFormValues((prev) => ({ ...prev, ...updates }) as SeoFormValues),
  })

  const handleFieldChange = useCallback(
    (field: keyof AnonFormState) =>
      (
        event:
          | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          | { target: { value: unknown } },
      ) => {
        const value = String((event?.target as { value?: unknown })?.value ?? '')
        setFormValues((prev) => ({ ...prev, [field]: value }))
      },
    [],
  )

  const handleSeoFieldChange = useCallback(
    (field: keyof SeoFormValues) =>
      (
        event:
          | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          | { target: { value: unknown; type?: string; checked?: boolean } },
      ) => {
        const { target } = event
        const value =
          target.type === 'checkbox' && 'checked' in target ? target.checked : target.value
        setSeoFormValues((prev) => ({ ...prev, [field]: value }))
      },
    [],
  )

  const anonDirty = useMemo(
    () => ANON_FIELD_KEYS.some((key) => formValues[key] !== initialValues[key]),
    [formValues, initialValues],
  )
  const seoDirty = useMemo(
    () => SEO_FIELD_KEYS.some((key) => seoFormValues[key] !== seoInitialValues[key]),
    [seoFormValues, seoInitialValues],
  )
  const isDirty = anonDirty || seoDirty

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const hasAnyAnonValue = useMemo(
    () =>
      (formValues.anonShortDescription?.trim() ?? '') !== '' ||
      (formValues.anonDescription?.trim() ?? '') !== '',
    [formValues.anonShortDescription, formValues.anonDescription],
  )

  useEffect(() => {
    const ref = saveHandlerRefStable.current
    ref.current = async () => {
      const contentId = contentData?.id
      if (!contentId) return
      if (seoDirty) {
        await updateVideoContent({
          variables: {
            content: {
              id: contentId,
              seoTitle: seoFormValues.seoTitle || null,
              seoDescription: seoFormValues.seoDescription || null,
              seoKeywords: seoFormValues.seoKeywords || null,
              seoRobotsNoFollow: seoFormValues.seoRobotsNoFollow,
              seoRobotsNoIndex: seoFormValues.seoRobotsNoIndex,
            },
            generatePath: false,
          },
        })
      }
      if (anonDirty) {
        const input = {
          contentId,
          shortDescription: formValues.anonShortDescription || null,
          description: formValues.anonDescription || null,
          shortDescriptionFormat: formValues.anonShortDescriptionFormat || null,
          descriptionFormat: formValues.anonDescriptionFormat || null,
        }
        if (anon == null && hasAnyAnonValue) {
          await contentAnonCreate({ variables: { input } })
        } else {
          await contentAnonUpdate({ variables: { input } })
        }
      }
    }
    return () => {
      ref.current = null
    }
  }, [
    contentData?.id,
    anon,
    anonDirty,
    seoDirty,
    formValues.anonShortDescription,
    formValues.anonDescription,
    formValues.anonShortDescriptionFormat,
    formValues.anonDescriptionFormat,
    seoFormValues.seoTitle,
    seoFormValues.seoDescription,
    seoFormValues.seoKeywords,
    seoFormValues.seoRobotsNoFollow,
    seoFormValues.seoRobotsNoIndex,
    hasAnyAnonValue,
    contentAnonCreate,
    contentAnonUpdate,
    updateVideoContent,
  ])

  return (
    <Container className="content-detail__section anonymous-fields" maxWidth={false}>
      <SectionAccordion title="Content Logged Out" collapsed={true}>
        <Typography
          variant="h6"
          sx={{
            mt: 0,
            mb: 2,
            color: '#333',
            fontWeight: 600,
            borderBottom: '2px solid #e0e0e0',
            pb: 1,
          }}
        >
          Basic Information
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <TextareaWithCounter
              className="content-detail__input"
              label="Logged Out Short Description"
              name="anonShortDescription"
              value={formValues.anonShortDescription}
              onChange={handleFieldChange('anonShortDescription')}
              onBlur={() => {}}
              disabled={isViewOnly}
              error={false}
              helperText=""
              maxValue={150}
            />
            <TextInputFormatSelect
              className="content-detail__input"
              label="Logged Out Short Description Format"
              value={formValues.anonShortDescriptionFormat}
              name="anonShortDescriptionFormat"
              onChange={handleFieldChange('anonShortDescriptionFormat')}
              disabled={isViewOnly}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <TextareaWithCounter
              className="content-detail__input"
              label="Logged Out Description"
              name="anonDescription"
              value={formValues.anonDescription}
              onChange={handleFieldChange('anonDescription')}
              onBlur={() => {}}
              disabled={isViewOnly}
              error={false}
              helperText=""
              maxValue={4000}
              rows={10}
            />
            <TextInputFormatSelect
              className="content-detail__input"
              label="Logged Out Description Format"
              value={formValues.anonDescriptionFormat}
              name="anonDescriptionFormat"
              onChange={handleFieldChange('anonDescriptionFormat')}
              disabled={isViewOnly}
            />
          </Grid>
        </Grid>

        <Typography
          variant="h6"
          sx={{
            mt: 4,
            mb: 2,
            color: '#333',
            fontWeight: 600,
            borderBottom: '2px solid #e0e0e0',
            pb: 1,
          }}
        >
          SEO
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <MuiTextFieldWrapper
              className="content-detail__input"
              label="SEO Title"
              name="seoTitle"
              value={seoFormValues.seoTitle}
              onChange={handleSeoFieldChange('seoTitle')}
              disabled={isViewOnly}
              fullWidth
              variant="outlined"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <MuiTextFieldWrapper
              className="content-detail__input"
              label="SEO Description"
              name="seoDescription"
              value={seoFormValues.seoDescription}
              onChange={handleSeoFieldChange('seoDescription')}
              disabled={isViewOnly}
              fullWidth
              variant="outlined"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <MuiTextFieldWrapper
              className="content-detail__input"
              label="SEO Keywords (separated by commas)"
              name="seoKeywords"
              value={seoFormValues.seoKeywords}
              onChange={handleSeoFieldChange('seoKeywords')}
              disabled={isViewOnly}
              fullWidth
              variant="outlined"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={4}>
            <MuiCheckbox
              name="seoRobotsNoFollow"
              label="SEO Robots - No Follow"
              onChange={handleSeoFieldChange('seoRobotsNoFollow')}
              checked={seoFormValues.seoRobotsNoFollow}
              disabled={isViewOnly}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={4}>
            <MuiCheckbox
              name="seoRobotsNoIndex"
              label="SEO Robots - No Index"
              onChange={handleSeoFieldChange('seoRobotsNoIndex')}
              checked={seoFormValues.seoRobotsNoIndex}
              disabled={isViewOnly}
            />
          </Grid>
        </Grid>
      </SectionAccordion>
    </Container>
  )
}

export default AnonymousFieldsSection
